import React, { useState } from "react";
import './App.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import DOMPurify from 'dompurify'
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import CardContent from '@mui/material/CardContent';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import PhoneIcon from '@mui/icons-material/Phone';
import Divider from '@mui/material/Divider';
import { LinkPreview } from '@dhaiwat10/react-link-preview';
import ButtonGroup from '@mui/material/ButtonGroup';
import Form from 'react-bootstrap/Form'
import Fade from '@mui/material/Fade';
import Fab from '@mui/material/Fab';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { isMobile } from 'react-device-detect';

import AppSearchAPIConnector from "@elastic/search-ui-app-search-connector";

import {
  ErrorBoundary,
  Facet,
  SearchProvider,
  SearchBox,
  Results,
  PagingInfo,
  ResultsPerPage,
  Paging,
  Sorting,
  WithSearch
} from "@elastic/react-search-ui";

import "@elastic/react-search-ui-views/lib/styles/styles.css";

import {
  buildAutocompleteQueryConfig,
  buildFacetConfigFromConfig,
  buildSearchOptionsFromConfig,
  buildSortOptionsFromConfig,
  getConfig,
  getFacetFields
} from "./config/config-helper";

const { hostIdentifier, searchKey, endpointBase, engineName } = getConfig();
const connector = new AppSearchAPIConnector({
  searchKey,
  engineName,
  hostIdentifier,
  endpointBase
});


const theme = createTheme({
  status: {
    danger: '#e53e3e',
  },
  palette: {
    primary: {
      main: '#0971f1',
      darker: '#053e85',
    },
    neutral: {
      main: '#fff ',
      contrastText: '#fff',
    },
    success: {
      main: '#11c19e ',
      contrastText: '#fff',
    },
    scroll: {
      main: '#11c19e ',
      contrastText: '#fff',
      backgroundColor: '#11c19e '
    },
    tag: {
      main: '#f328c1 ',
      contrastText: '#fff',
    },
  }
});

const compThemes = createTheme({
  components: {
    // Name of the component
    MuiCard: {
      styleOverrides: {
        // Name of the slot
        root: {
          // Some CSS
          position: 'sticky',
          top: 0,
          overflow: 'overlay '
        },
      },
    },
  },
});


const config = {
  searchQuery: {
    facets: buildFacetConfigFromConfig(),
    ...buildSearchOptionsFromConfig()
  },
  autocompleteQuery: buildAutocompleteQueryConfig(),
  apiConnector: connector,
  alwaysSearchOnInitialLoad: true,
}

function ScrollTop(props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top-anchor',
    );

    if (anchor) {
      anchor.scrollIntoView({
        block: 'center',
      });
    }
  };

  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 60, right: 16 }}
      >
        {children}
      </Box>
    </Fade>
  );
}

ScrollTop.propTypes = {
  children: PropTypes.element.isRequired,
  window: PropTypes.func,
};

const FailedPreview = () => {

  return <>
    <Card
      height={!isMobile ? '400px' : '200px'}
      width="100%"
      sx={{ backgroundColor: 'white', color: 'white' }}>
      <CardMedia
        component="img"
        height="400px"
        image="/no-preview.png"
        alt='fallabck"'
      />
    </Card>
  </>
}

export default function App() {

  const [history, addHistory] = useState([])

  const CustomResultView = ({
    result,
    onClickLink,
  }) => {

    const results = <li key={result.key.raw} className="sui-result" style={{ position: 'relative' }}>
      <Container>
        <Row>
          <Typography variant="h6" gutterBottom component="div">
            <a style={{ color: '#11c19e' }} onClick={onClickLink} href={result.data_url.raw} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.data_title?.snippet) }}></a>
          </Typography>
        </Row>
        <Row >
          <Typography variant="body2"
            gutterBottom
            component="div">
            <i style={{ fontStyle: 'italic' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.data_url?.snippet) }}></i>
          </Typography>
        </Row>
        <Divider variant="middle" light={true} className="mt-3 mb-3" />
      </Container>
      <Row>
        <Col xl={6}>
          <Container>
            <ErrorBoundary>
              {result.data_url?.snippet !== null &&
                <LinkPreview url={result.data_url?.snippet}
                  width='100%'
                  height={!isMobile ? '400px' : '200px'}
                  showPlaceholderIfNoImage={true}
                  className="mt-3"
                  alt="preview"
                  fallback={<FailedPreview />}
                  customFetcher={{ title: result.data_url?.snippet }}
                />
              }
            </ErrorBoundary>
          </Container>
        </Col>
        <Col xl={6}>
          <Container>
            <Row>
              <Typography variant="body1" gutterBottom component="div" className="mt-3">
                Document Tags:<br />
                {!Array.isArray(result.data_tags.raw) && result.data_tags.raw !== null ?
                  <ThemeProvider theme={theme}>
                    <Button size="small" color="tag" variant="outlined" disabled>{result.data_tags.raw}</Button>
                  </ThemeProvider>
                  :
                  <>
                    {result.data_tags.raw.map(tag => (
                      <>
                        <ThemeProvider theme={theme}>
                          <ButtonGroup variant="contained" aria-label="outlined primary button group">
                            <SearchBox
                              className="mt-3 mb-3"
                              view={({ onChange, onSubmit }) => (
                                <Form onSubmit={onSubmit}>
                                  <Button
                                    variant="outlined"
                                    color="tag"
                                    sx={{ fontSize: 12, mt: 1, ml: 1, color: "white" }}
                                    type="submit"
                                    value={tag}
                                    onClick={(e) => {
                                      onChange(e.target.value)
                                      setTimeout(() => {
                                        if (!history.includes(e.target.value)) {
                                          addHistory([...history, e.target.value])
                                        }
                                      }, 1000);
                                    }}
                                  >{tag}</Button>

                                </Form>
                              )}
                            />
                          </ButtonGroup>
                        </ThemeProvider>
                      </>
                    ))
                    }
                  </>
                }
              </Typography>
            </Row>
            <Row>
              <Typography sx={{ fontSize: 16, mt: 2 }} gutterBottom component="div">
                File(s): <Button onClick={onClickLink} href={result.links_alternate_href.raw}><img src="/logo.png" alt="logo" width="50" height="35" /></Button>
              </Typography>
            </Row>
            <Row>
              {result.text.snippet && <>
                <Typography sx={{ fontSize: 16, mt: 2 }} gutterBottom component="div">
                  Preview:<br /><br /> {result.text.snippet}
                </Typography>
              </>}
            </Row>
          </Container>
        </Col>
      </Row>
    </li >

    return results
  };

  return <>
    {!isMobile &&
      <>
        <Container maxWidth="lg">
          <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" sx={{ backgroundColor: 'black' }}>
              <Toolbar id="back-to-top-anchor">
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ flexGrow: 1, fontWeight: 500 }}>
                  <a href="https://www.unsilenced.org/"><img src="/unsilenced2.jfif" alt="logo" style={{ height: '100px', width: "100px" }} /></a>
                </Typography>
                <ThemeProvider theme={theme}>
                  <div>
                    <Button type="submit" aria-label="facebook" href="https://www.facebook.com/Unsilenced.now"><FacebookIcon sx={{ mr: 2 }} color="neutral" /></Button>
                    <Button type="submit" aria-label="twitter" href="https://twitter.com/UnSilenced_Now"><TwitterIcon sx={{ mr: 2 }} color="neutral" /></Button>
                    <Button type="submit" aria-label="instagram" href="https://www.instagram.com/UnSilenced_Now/"><InstagramIcon sx={{ mr: 2 }} color="neutral" /></Button>
                    <Button type="submit" aria-label="youtube" href="https://www.youtube.com/c/UnSilenced_Now"><YouTubeIcon sx={{ mr: 2 }} color="neutral" /></Button>
                    <Button type="submit" aria-label="linkedin" href="https://www.linkedin.com/company/weareunsilenced/"><LinkedInIcon sx={{ mr: 2 }} color="neutral" /></Button>
                    <Button type="submit" aria-label="mail" href="mailto: info@unsilenced.org"><EmailIcon sx={{ mr: 2 }} color="neutral" /></Button>
                    <Button type="submit" aria-label="tele" href="tel:1-800-525-1209"><PhoneIcon sx={{ mr: 5, }} color="neutral" /></Button>
                  </div>
                </ThemeProvider>
                <Button variant="contained"
                  sx={{
                    backgroundColor: '#8517BF',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#f328c1',
                      color: 'white ',
                    }
                  }}
                  href="https://www.paypal.com/donate/?hosted_button_id=QCJRJ768JJPBS">Donate
                </Button>
              </Toolbar>
            </AppBar>
          </Box>
        </Container>
        <>
          <SearchProvider config={config} >
            <WithSearch mapContextToProps={({ wasSearched, rawResponse, results }) => ({ wasSearched, rawResponse, results })}>

              {({ wasSearched }) => {

                return (
                  <div className="App">
                    <Divider variant="middle" light={true} className=" mb-3" />
                    <Container maxWidth="xl">
                      <ErrorBoundary>
                        <Row>
                          <Col xl={3}>
                            <ThemeProvider theme={compThemes}>
                              <Card sx={{ maxWidth: 700, minHeight: 100, mb: 1, mt: 3, height: 900 }}>
                                <CardContent>
                                  <Row>
                                    <SearchBox
                                      sx={{ mt: 3 }}
                                      view={({ onChange, value, onSubmit }) => (
                                        <Form onSubmit={onSubmit}
                                          style={{ width: '100%' }}>
                                          <FormControl >
                                            <InputLabel htmlFor="seachInput">Search</InputLabel>
                                            <Input
                                              id="seachInput"
                                              type="input"
                                              value={value}
                                              onChange={(e) => {
                                                onChange(e.target.value)
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  setTimeout(() => {
                                                    if (!history.includes(e.target.value)) {
                                                      addHistory([...history, e.target.value])
                                                    }
                                                  }, 1000)
                                                }
                                              }}

                                            />
                                          </FormControl>
                                        </Form>
                                      )}
                                    />
                                  </Row>
                                  <Row>
                                    <div className="mb-3">
                                      <br />
                                      {history.length > 0 &&

                                        <>
                                          HISTORY
                                          <Button variant="text"
                                            sx={{ fontSize: 10, color: "white" }}
                                            type="button"
                                            value={''}
                                            onClick={(e) => {
                                              addHistory([])
                                            }}
                                          >-Clear-</Button>
                                          <br />
                                          {history.map((tag, index) => (
                                            <ButtonGroup key={index} variant="contained" aria-label="outlined primary button group">
                                              <>
                                                <ThemeProvider theme={theme}>
                                                  <SearchBox
                                                    className="mt-1 mb-1"
                                                    view={({ onChange, onSubmit }) => (
                                                      <Form onSubmit={onSubmit}>
                                                        <Button
                                                          variant="outlined"
                                                          color="tag"
                                                          sx={{ fontSize: 10, mt: 1, ml: 1, color: "white" }}
                                                          type="submit"
                                                          value={tag}
                                                          onClick={(e) => {
                                                            onChange(e.target.value)

                                                          }}
                                                        >{tag}</Button>

                                                      </Form>
                                                    )}
                                                  />
                                                </ThemeProvider>
                                              </>
                                            </ButtonGroup>

                                          ))
                                          }
                                          <Divider variant="middle" light={true} className="mt-3 mb-3" />
                                        </>
                                      }

                                    </div>
                                  </Row>
                                  <Row>
                                    {getFacetFields().map(field => (
                                      <Facet key={field} field={field} label={field} />
                                    ))}
                                  </Row>
                                  {wasSearched && (
                                    <>
                                      <Row>
                                        <Sorting
                                          label={"Sort by"}
                                          sortOptions={buildSortOptionsFromConfig()}
                                          className="mt-3"
                                        />
                                      </Row>
                                    </>
                                  )}
                                </CardContent>
                              </Card>
                            </ThemeProvider>
                          </Col>
                          <Col xl={9}>
                            <Row>
                              <Card>
                                <CardContent>
                                  {wasSearched &&
                                    <Row>
                                      <Col xl={10}>
                                        <PagingInfo />
                                      </Col>
                                      <Col xl={2}>
                                        <ResultsPerPage className="mb-3" />
                                      </Col>
                                    </Row>}
                                  <Results
                                    resultView={CustomResultView}
                                    shouldTrackClickThrough={true}
                                    clickThroughTags={['Texas']}
                                  />
                                </CardContent>
                              </Card>
                            </Row>
                            <Row>
                              <Col xl={8} className="d-flex justify-content-end">
                                <Paging sx={{
                                  color: 'white',
                                  "&:hover": {
                                    color: "black",
                                  }
                                }} />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </ErrorBoundary>
                    </Container>
                  </div>
                );
              }}
            </WithSearch >
          </SearchProvider >
          <ScrollTop>
            <Fab size="small" aria-label="scroll back to top">
              <KeyboardArrowUpIcon />
            </Fab>
          </ScrollTop>

        </>
      </>}
    {isMobile &&
      <>
        <Container maxWidth="lg">
          <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" sx={{ backgroundColor: 'black' }}>
              <Toolbar id="back-to-top-anchor">
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ flexGrow: 1, fontWeight: 500 }}>
                  <a href="https://www.unsilenced.org/"><img src="/unsilenced2.jfif" alt="logo" style={{ height: '100px', width: "100px" }} /></a>
                </Typography>
                <Button variant="contained"
                  sx={{
                    backgroundColor: '#8517BF',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#f328c1',
                      color: 'white ',
                    }
                  }}
                  href="https://www.paypal.com/donate/?hosted_button_id=QCJRJ768JJPBS">Donate
                </Button>
              </Toolbar>
            </AppBar>
          </Box>
        </Container>
        <>
          <Row>
            <Col className="d-flex justify-content-center">
              <ThemeProvider theme={theme}>
                <Button type="submit" aria-label="facebook" href="https://www.facebook.com/Unsilenced.now"><FacebookIcon color="neutral" /></Button>
                <Button type="submit" aria-label="twitter" href="https://twitter.com/UnSilenced_Now"><TwitterIcon color="neutral" /></Button>
                <Button type="submit" aria-label="instagram" href="https://www.instagram.com/UnSilenced_Now/"><InstagramIcon color="neutral" /></Button>
                <Button type="submit" aria-label="youtube" href="https://www.youtube.com/c/UnSilenced_Now"><YouTubeIcon color="neutral" /></Button>
                <Button type="submit" aria-label="mail" href="mailto: info@unsilenced.org"><EmailIcon color="neutral" /></Button>
                <Button type="submit" aria-label="tele" href="tel:1-800-525-1209"><PhoneIcon color="neutral" /></Button>
              </ThemeProvider>
            </Col>
          </Row>
          <SearchProvider config={config} >
            <WithSearch mapContextToProps={({ wasSearched, rawResponse, results }) => ({ wasSearched, rawResponse, results })}>

              {({ wasSearched }) => {

                return (
                  <div className="App">
                    <Divider variant="middle" light={true} className=" mb-3 mt-3" />
                    <Container maxWidth="xl">
                      <ErrorBoundary>
                        <Row>
                          <Col xl={12}>
                            <Row>
                              <Col className="d-flex justify-content-center mb-3 mt-3">
                                <PagingInfo />
                              </Col>
                            </Row>
                            <Row>
                              <SearchBox
                                sx={{ mt: 3, mb: 3 }}
                                view={({ onChange, value, onSubmit }) => (
                                  <Form onSubmit={onSubmit}
                                    style={{ width: '100%' }}>
                                    <FormControl >
                                      <InputLabel htmlFor="seachInput">Search</InputLabel>
                                      <Input
                                        id="seachInput"
                                        type="input"
                                        value={value}
                                        onChange={(e) => {
                                          onChange(e.target.value)
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            setTimeout(() => {
                                              if (!history.includes(e.target.value)) {
                                                addHistory([...history, e.target.value])
                                              }
                                            }, 1000)
                                          }
                                        }}

                                      />
                                    </FormControl>
                                  </Form>
                                )}
                              />
                            </Row>
                            <Row>
                              <Col>
                                <Accordion fullWidth={true} className="mt-3 mb-3">
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                                    aria-controls="panel1a-content"
                                    id="panel1a-header">
                                    <Typography variant="title">Filters</Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    {getFacetFields().map(field => (
                                      <Facet key={field} field={field} label={field} />
                                    ))}
                                  </AccordionDetails>
                                </Accordion>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <Results
                                  resultView={CustomResultView}
                                  shouldTrackClickThrough={true}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col className="d-flex justify-content-end">
                                <Paging sx={{
                                  color: 'white',
                                  "&:hover": {
                                    color: "black",
                                  }
                                }} />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </ErrorBoundary>
                    </Container>
                  </div>
                );
              }}
            </WithSearch >
          </SearchProvider >
          <ScrollTop>
            <Fab size="small" aria-label="scroll back to top">
              <KeyboardArrowUpIcon />
            </Fab>
          </ScrollTop>

        </>
      </>}
  </>
}
